import _ from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@/redux/rootReducer';
import {
    getConsultationReasons,
    selectConsultationById,
    selectConsultationReasons,
    updateConsultation,
} from '@/redux/reducers/consultation.reducer';
import {
    createSpecialityOption,
    getSpecialityOptions,
    latestCreatedSpecialityOptionIdSelector,
    selectSpecialityOptionCreateRequestTracker,
    selectSpecialityOptions,
} from '@/redux/reducers/speciality-option.reducer';
import { SpecialityAutocompleteOption } from '@/components/ui/autocomplete/SpecialitiesAutoComplete';
import { useRequestHandler } from '@/hooks/useRequestHandler';
import { useDebouncedCallback } from 'use-debounce';
import ConsultationInfoComponent from '../components/ConsultationInfo';

type ConsultationInfoProps = {
    consultationId?: string;
};

export function ConsultationInfo(props: ConsultationInfoProps) {
    const { consultationId } = props;
    const dispatch = useAppDispatch();

    const consultation = useAppSelector((state) => (consultationId ? selectConsultationById(state, consultationId) : undefined))
    const consultationReasons = useAppSelector(selectConsultationReasons);

    const reasons = useMemo(() => _.map(consultationReasons, 'name'), [consultationReasons]);
    const specialities = useAppSelector(selectSpecialityOptions);

    const [selectedSpeciality, setSelectedSpeciality] = useState<SpecialityAutocompleteOption | null>(null);

    // Update selectedSpeciality once specialities are loaded and we have the consultation data
    useEffect(() => {
        if (specialities.length > 0 && consultation && consultation.speciality !== selectedSpeciality?.name) {
            const matchedSpeciality = specialities.find(speciality => speciality.name === consultation.speciality);
            setSelectedSpeciality(matchedSpeciality || null);
        }
    }, [specialities, consultation, selectedSpeciality?.name]);

    const [reason, setReason] = useState<string>(consultation?.reason || '');

    const handleConsultationUpdate = useDebouncedCallback((values: { reason?: string; speciality?: string }) => {
        if (!consultationId) return;
        dispatch(updateConsultation(consultationId, values));
    }, 1000);


    useEffect(() => {
        dispatch(getConsultationReasons());
        dispatch(getSpecialityOptions());
    }, [dispatch]);

    const handleReasonChange = useCallback((newReason: string) => {
        setReason(newReason);
        handleConsultationUpdate({ reason: newReason });
    }, [handleConsultationUpdate]);

    const latestCreatedSpeciality = useAppSelector(latestCreatedSpecialityOptionIdSelector)

    const { dispatch: createSpecialityOptionDispatch } = useRequestHandler({
        onSuccess: () => {
            // Reload the speciality options after successful creation
            dispatch(getSpecialityOptions({}))
            // Add the newly created speciality to the formik field
            const value = specialities.find((speciality) => speciality._id === latestCreatedSpeciality) as SpecialityAutocompleteOption
            if (value && consultationId) {
                setSelectedSpeciality(value)
                dispatch(updateConsultation(consultationId, { speciality: value.name }));
            }
        },
        onError: () => { },
        requestTrackerSelector: selectSpecialityOptionCreateRequestTracker,
    })

    const handleSetSpecialityValue = useCallback((value: SpecialityAutocompleteOption | null | undefined) => {
        if (!value) return
        setSelectedSpeciality(value)
        if (value) {
            setSelectedSpeciality(value)
        } else {
            setSelectedSpeciality(null)
        }
    }, [setSelectedSpeciality])

    const handleSpecialityChange = (
        event: any,
        newValue: (string | SpecialityAutocompleteOption)[] | SpecialityAutocompleteOption | string | null,
        typeOfEvent: string
    ) => {
        if (Array.isArray(newValue)) {
            return
        }
        if (typeOfEvent === 'removeOption' || typeOfEvent === 'clear') {
            handleSetSpecialityValue(null)
            return
        }
        if (typeOfEvent === 'createOption') {
            // find the string in the newValue array
            if (!newValue) {
                return
            }
            if (typeof newValue !== 'string') {
                return
            }
            createSpecialityOptionDispatch(
                createSpecialityOption({
                    name: newValue,
                    code: 'CREATED.' + newValue.toLowerCase().replace(/ /g, '-'),
                })
            )
            return
        }
        if (typeOfEvent === 'selectOption') {
            // check wether added option contains "Add" in it
            if (typeof newValue === 'string' || !newValue) {
                return
            }
            if (newValue.isNew) {
                if (!newValue.inputValue) {
                    return
                }
                createSpecialityOptionDispatch(
                    createSpecialityOption({
                        name: (newValue).inputValue,
                        code: 'CREATED.' + newValue.inputValue.toLowerCase().replace(/ /g, '-'),
                    })
                )
                return
            }
        }
        if (typeof newValue === 'string' || !newValue) {
            return
        }
        setSelectedSpeciality(newValue)
        if (consultationId) {
            dispatch(updateConsultation(consultationId, { speciality: newValue.name }));
        }
    }

    const handleInputChange = useDebouncedCallback((newInputValue: string) => {
        dispatch(getSpecialityOptions({ smartSearch: newInputValue }))
    }, 500)



    return (
        <ConsultationInfoComponent
            selectedSpeciality={selectedSpeciality}
            specialities={specialities}
            onInputChange={handleInputChange}
            onSpecialityChange={handleSpecialityChange}
            reason={reason}
            reasons={reasons}
            onReasonChange={handleReasonChange}
            patientLocation={consultation?.patientLocation}
        />
    );
}
